import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { folderService } from '../services/folder'
import type {
  Folder,
  FolderListItem,
  CreateFolder,
  UpdateFolder,
  FolderSearchParams,
  PaginationParams,
  ApiResponse,
  PaginatedResponse
} from '../types'

// Query keys
export const folderKeys = {
  all: ['folders'] as const,
  lists: () => [...folderKeys.all, 'list'] as const,
  list: (params: PaginationParams) => [...folderKeys.lists(), params] as const,
  details: () => [...folderKeys.all, 'detail'] as const,
  detail: (id: string) => [...folderKeys.details(), id] as const,
  searches: () => [...folderKeys.all, 'search'] as const,
  search: (params: FolderSearchParams) => [...folderKeys.searches(), params] as const
}

// Hook to fetch folders with pagination
export function useFolders(params: PaginationParams = { page: 1, size: 10 }) {
  return useQuery<ApiResponse<PaginatedResponse<FolderListItem>>, Error>({
    queryKey: folderKeys.list(params),
    queryFn: () => folderService.getFolders(params)
  })
}

// Hook to fetch a single folder by ID
export function useFolder(folderId: string) {
  return useQuery<ApiResponse<FolderListItem>, Error>({
    queryKey: folderKeys.detail(folderId),
    queryFn: () => folderService.getFolderById(folderId),
    enabled: !!folderId
  })
}

// Hook to search folders
export function useSearchFolders(params: FolderSearchParams) {
  return useQuery<ApiResponse<PaginatedResponse<Folder>>, Error>({
    queryKey: folderKeys.search(params),
    queryFn: () => folderService.searchFolders(params),
    enabled: !!params.searchBy
  })
}

// Hook to create a folder
export function useCreateFolder() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Folder>, Error, CreateFolder>({
    mutationFn: (data: CreateFolder) => folderService.createFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() })
    }
  })
}

// Hook to update a folder
export function useUpdateFolder() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { folderId: string; data: UpdateFolder }
  >({
    mutationFn: ({ folderId, data }) =>
      folderService.updateFolder(folderId, data),
    onSuccess: (_, { folderId }) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.detail(folderId) })
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: folderKeys.searches() })
    }
  })
}

// Hook to delete a folder
export function useDeleteFolder() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: (folderId: string) => folderService.deleteFolder(folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: folderKeys.searches() })
    }
  })
}
